import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useAssessmentStore } from '../store';
import { getAvatarName } from '../data/avatarMappings';

interface AvatarResultsProps {
  onBack: () => void;
  avatarIndex: number;
}

const AvatarResults: React.FC<AvatarResultsProps> = ({ onBack, avatarIndex }) => {
  const { dassResults, brsResults, cissResults } = useAssessmentStore();

  if (!dassResults || !brsResults || !cissResults) {
    return null;
  }

  // Get avatar name using the comprehensive mapping
  const avatarName = getAvatarName(
    dassResults.depression.severity,
    dassResults.anxiety.severity,
    dassResults.stress.severity,
    brsResults.severity,
    cissResults.primaryStyle
  );

  // Prepare data for the radar chart
  const data = [
    { 
      subject: '憂鬱',
      value: ((42 - dassResults.depression.score) / 42) * 100,
      fullMark: 100 
    },
    { 
      subject: '焦慮',
      value: ((42 - dassResults.anxiety.score) / 42) * 100,
      fullMark: 100 
    },
    { 
      subject: '壓力',
      value: ((42 - dassResults.stress.score) / 42) * 100,
      fullMark: 100 
    },
    { 
      subject: '心理韌性',
      value: (brsResults.score / 5) * 100,
      fullMark: 100 
    },
    { 
      subject: '應對能力',
      value: (cissResults.task.percentage + cissResults.emotion.percentage) / 2,
      fullMark: 100 
    }
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">你的AI形象</h2>
        </div>

        <div className="space-y-8">
          {/* Avatar Display */}
          <div className="flex justify-center">
            <div className="relative w-48 h-48 rounded-full overflow-hidden border-4 border-blue-500">
              <img
                src={`https://raw.githubusercontent.com/stackblitz/stackblitz-avatars/main/${avatarIndex + 1}.png`}
                alt="AI Avatar"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Avatar Name */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-blue-600">{avatarName}</h3>
            <p className="text-gray-600 mt-2">基於你的評估結果生成的專屬AI形象</p>
          </div>

          {/* Radar Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">心理特質分布</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis angle={72} domain={[0, 100]} />
                  <Radar
                    name="能力值"
                    dataKey="value"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">特質總結</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-32 text-sm font-medium text-blue-800">情緒狀態</div>
                <div className="flex-1">
                  <div className={`inline-block px-3 py-1 rounded-full text-sm ${dassResults.depression.color}`}>
                    {dassResults.depression.severity}
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-32 text-sm font-medium text-blue-800">心理韌性</div>
                <div className="flex-1">
                  <div className={`inline-block px-3 py-1 rounded-full text-sm ${brsResults.color}`}>
                    {brsResults.severity}
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-32 text-sm font-medium text-blue-800">主要應對方式</div>
                <div className="flex-1">
                  <div className="inline-block px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800">
                    {cissResults.primaryStyle === 'task' ? '任務導向' :
                     cissResults.primaryStyle === 'emotion' ? '情緒導向' : '迴避導向'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Characteristics */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">優勢特質</h4>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {cissResults.primaryStyle === 'task' ? '善於解決問題' :
                   cissResults.primaryStyle === 'emotion' ? '情感豐富細膩' : '靈活應變'}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {brsResults.severity.includes('高') ? '抗壓性強' : '穩定成長'}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  適應力佳
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-lg p-6">
              <h4 className="font-semibold text-amber-900 mb-3">發展方向</h4>
              <ul className="space-y-2 text-amber-800">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  強化情緒管理能力
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  培養多元應對策略
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  建立支持系統
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarResults;